import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Zap, Copy, Check, Mail, Linkedin, Twitter, MessageSquare, Send,
    ChevronRight
} from 'lucide-react';
import Navbaar from '../../components/Navbar/Navbaar';
import Sidebar from '../../components/Navbar/SideNavbar';
import api from '../../api/axios';

// Status → badge label/color, sub-label, and reward-column treatment.
const STATUS_META = {
    reward_earned: { badge: 'Reward earned', sub: 'Bought credits',  badgeCls: 'bg-emerald-50 text-emerald-700', kind: 'earned' },
    signed_up:     { badge: 'Signed up',     sub: 'Created account', badgeCls: 'bg-blue-50 text-blue-600',      kind: 'pending' },
    sent:          { badge: 'Sent',          sub: 'Invite sent',     badgeCls: 'bg-slate-100 text-slate-500',   kind: 'none' },
};

const HOW_IT_WORKS = [
    { t: 'Share your link', d: 'Your unique referral URL — anywhere you like.' },
    { t: 'Friend starts free trial', d: 'They get a 7-day free trial of Pro. No card required.' },
    { t: 'They buy credits', d: 'After the trial, when they buy their first credits, you earn.' },
    { t: 'Credits hit your wallet', d: '10% of their first purchase, in credits. Stackable, never expire.' },
];

const FAQS = [
    {
        q: 'When do my referral credits arrive?',
        a: 'The moment your friend makes their first credit purchase (after their 7-day free trial), your reward is added to your balance automatically — there\'s nothing to claim.',
    },
    {
        q: 'How much do I earn per friend?',
        a: 'You earn 10% of your friend\'s first credit purchase, paid in credits. For example, if they buy 1,000 credits you get 100. It\'s a one-time reward per friend.',
    },
    {
        q: 'Can I refer myself with another email?',
        a: 'No. Self-referrals are blocked, and disposable or temporary email addresses can\'t sign up — so alt-email referrals won\'t earn credits.',
    },
    {
        q: 'Do my credits expire?',
        a: 'Never. Earned credits stay in your balance, stack across every referral, and are ready to spend anywhere in Career Champ.',
    },
    {
        q: 'Is there a limit on how many friends I can refer?',
        a: 'No cap. Invite as many people as you like and earn 10% on each one\'s first purchase.',
    },
];

const initials = (s) => String(s || '?').trim().split(/\s+/).map((x) => x[0]).join('').slice(0, 2).toUpperCase();
const avatarBg = (s) => { const c = ['#7C3AED', '#3F57E0', '#0D9488', '#DB2777', '#D97706', '#10B981', '#2563EB']; let h = 0; for (const ch of String(s || '')) h = ch.charCodeAt(0) + ((h << 5) - h); return c[Math.abs(h) % c.length]; };

const timeAgo = (date) => {
    if (!date) return '';
    const diff = Date.now() - new Date(date).getTime();
    const m = Math.floor(diff / 60000), h = Math.floor(m / 60), d = Math.floor(h / 24);
    if (d === 1) return 'Yesterday';
    if (d > 1) return `${d} days ago`;
    if (h > 0) return `${h} ${h === 1 ? 'hour' : 'hours'} ago`;
    if (m > 0) return `${m} min ago`;
    return 'Just now';
};

const HeroStat = ({ label, value, sub }) => (
    <div className="rounded-2xl bg-white/10 border border-white/15 px-4 py-3.5">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-white/70">{label}</p>
        <p className="text-[26px] font-extrabold text-white leading-none mt-1.5">{value}</p>
        <p className="text-[11px] text-white/60 mt-1.5">{sub}</p>
    </div>
);

const ReferEarn = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [inviteInput, setInviteInput] = useState('');
    const [inviting, setInviting] = useState(false);
    const [toast, setToast] = useState(null);
    const [filter, setFilter] = useState('all'); // all | earned | pending
    const [openFaq, setOpenFaq] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const toastTimer = useRef(null);

    const showToast = (m) => {
        clearTimeout(toastTimer.current);
        setToast(m);
        toastTimer.current = setTimeout(() => setToast(null), 2500);
    };
    useEffect(() => () => clearTimeout(toastTimer.current), []);

    const load = async () => {
        try {
            const res = await api.get('/api/referrals/me');
            setData(res.data);
        } catch (e) {
            showToast('Could not load your referral data');
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const link = data?.link || '';
    const displayLink = link.replace(/^https?:\/\//, '');
    const stats = data?.stats || { invited: 0, joined: 0, earned: 0, lifetimePayout: 0 };
    const referrals = data?.referrals || [];

    const commissionPct = Math.round((data?.offer?.commissionRate ?? 0.10) * 100);
    const trialDays = data?.offer?.trialDays ?? 7;
    const creditUsd = data?.offer?.creditUsd ?? 0.08;
    const payoutUsd = stats.lifetimePayout ?? stats.earned * creditUsd;

    const earnedCount = referrals.filter((r) => r.status === 'reward_earned').length;
    const pendingCount = referrals.filter((r) => r.status === 'signed_up').length;
    // Share of invited friends who bought credits — matches the tile it sits under.
    const purchaseConversion = stats.invited ? Math.round((earnedCount / stats.invited) * 100) : 0;

    const filtered = useMemo(() => {
        if (filter === 'earned') return referrals.filter((r) => r.status === 'reward_earned');
        if (filter === 'pending') return referrals.filter((r) => r.status !== 'reward_earned');
        return referrals;
    }, [referrals, filter]);
    const shown = showAll ? filtered : filtered.slice(0, 6);

    const handleCopy = async () => {
        if (!link) return;
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {
            showToast('Copy failed — select the link and copy it manually');
        }
    };

    const shareMsg = `Join me on Career Champ — land your next role faster. Get a ${trialDays}-day free trial of Pro: ${link}`;

    const shareTargets = useMemo(() => {
        const enc = encodeURIComponent;
        return [
            { name: 'Email', icon: Mail, cls: 'bg-[#64748B] hover:bg-[#475569]', href: `mailto:?subject=${enc('Join me on Career Champ')}&body=${enc(shareMsg)}` },
            { name: 'LinkedIn', icon: Linkedin, cls: 'bg-[#0A66C2] hover:brightness-110', href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(link)}` },
            { name: 'X / Twitter', icon: Twitter, cls: 'bg-[#1E293B] hover:bg-[#0F172A]', href: `https://twitter.com/intent/tweet?text=${enc(shareMsg)}` },
        ];
    }, [link, shareMsg]);

    // Slack has no share URL that can pre-fill a message (unlike Twitter/LinkedIn),
    // so we copy the message, then open the Slack desktop app — falling back to
    // Slack on the web if the app doesn't take focus.
    const handleSlackShare = async () => {
        try {
            await navigator.clipboard.writeText(shareMsg);
            showToast('Message copied — paste it in Slack');
        } catch {
            showToast('Copy failed — select the link and copy it manually');
        }
        const fallback = setTimeout(() => {
            window.open('https://app.slack.com', '_blank', 'noopener,noreferrer');
        }, 900);
        const cancelFallback = () => { clearTimeout(fallback); window.removeEventListener('blur', cancelFallback); };
        window.addEventListener('blur', cancelFallback);
        window.location.href = 'slack://open';
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        const emails = inviteInput.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
        if (!emails.length) return;
        setInviting(true);
        try {
            const res = await api.post('/api/referrals/invite', { emails });
            setData((d) => ({ ...d, referrals: res.data.referrals, stats: res.data.stats }));
            setInviteInput('');
            const n = res.data.invited;
            showToast(n === 0
                ? 'No new invites — those friends were already invited'
                : `Invited ${n} ${n === 1 ? 'person' : 'people'}`);
        } catch (e2) {
            showToast(e2.response?.data?.message || 'Could not send invites');
        } finally { setInviting(false); }
    };

    return (
        <div className="relative h-screen bg-[#F6F7FB] pt-16 flex flex-col">
            <div className="flex w-full"><Navbaar /></div>
            <div className="flex flex-row flex-1 min-h-0">
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0"><Sidebar /></div>

                <div className="flex flex-1 overflow-y-auto w-full">
                    <div className="w-full">
                        {toast && (
                            <div className="fixed top-20 right-6 z-50 px-4 py-2.5 rounded-xl bg-[#0F172A] text-white text-[13px] font-medium shadow-lg">{toast}</div>
                        )}

                        <div className="px-6 md:px-8 py-6 flex flex-col gap-5 text-left">
                            {/* Hero */}
                            <div className="rounded-3xl p-7 md:p-8 relative overflow-hidden" style={{ background: 'linear-gradient(120deg,#3B2E8F 0%,#3F47C4 45%,#4F63E8 100%)' }}>
                                <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
                                    {/* Left */}
                                    <div className="min-w-0 text-left">
                                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-white/90 bg-white/10 border border-white/15 rounded-full px-3 py-1">
                                            <Zap size={13} /> Referral program
                                        </span>
                                        <h2 className="text-[30px] md:text-[34px] font-extrabold text-white mt-4 leading-[1.1]">
                                            Give a {trialDays}-day free trial, earn <span className="text-amber-300">{commissionPct}% in credits</span> per friend.
                                        </h2>
                                        <p className="text-white/75 text-[14px] mt-3 max-w-xl leading-relaxed">
                                            Send your link. Friends get a {trialDays}-day free trial of Pro. When they buy their first credits, you earn {commissionPct}% of that purchase in credits — stackable, never expire.
                                        </p>

                                        {/* Link + copy */}
                                        <div className="mt-6 flex flex-col sm:flex-row gap-2.5 max-w-xl">
                                            <div className="flex-1 flex items-center bg-white/10 border border-white/20 rounded-xl px-4 py-3 min-w-0">
                                                <span className="text-[13.5px] text-white truncate flex-1">{loading ? 'Loading your link…' : displayLink}</span>
                                            </div>
                                            <button onClick={handleCopy} disabled={!link}
                                                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white text-[#3F57E0] text-[13.5px] font-bold hover:bg-indigo-50 transition-colors disabled:opacity-60 shrink-0">
                                                {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Copied' : 'Copy link'}
                                            </button>
                                        </div>

                                        {/* Share buttons */}
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {shareTargets.map((s) => (
                                                <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer"
                                                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-white text-[12.5px] font-semibold transition-all ${s.cls}`}>
                                                    <s.icon size={14} /> Share on {s.name}
                                                </a>
                                            ))}
                                            <button type="button" onClick={handleSlackShare}
                                                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-white text-[12.5px] font-semibold transition-all bg-[#611f69] hover:brightness-125">
                                                <MessageSquare size={14} /> Share on Slack
                                            </button>
                                        </div>
                                    </div>

                                    {/* Right — stat grid */}
                                    <div className="grid grid-cols-2 gap-3 content-start">
                                        <HeroStat label="Friends invited" value={stats.invited} sub="all time" />
                                        <HeroStat label="Bought credits" value={earnedCount} sub={`${purchaseConversion}% of invited`} />
                                        <HeroStat label="Credits earned" value={stats.earned.toLocaleString()} sub="added to your balance" />
                                        <HeroStat label="Lifetime payout" value={`$${payoutUsd % 1 === 0 ? payoutUsd : payoutUsd.toFixed(2)}`} sub={`@ $${creditUsd} / credit`} />
                                    </div>
                                </div>
                            </div>

                            {/* Body grid */}
                            <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
                                {/* Referrals */}
                                <div className="bg-white rounded-2xl border border-[#ECECEC] p-5">
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div>
                                            <h3 className="text-[15px] font-bold text-slate-900">Your referrals</h3>
                                            <p className="text-[12px] text-slate-400 mt-0.5">{stats.invited} invited · {earnedCount} earned credits · {pendingCount} still pending</p>
                                        </div>
                                        <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                                            {['all', 'earned', 'pending'].map((f) => (
                                                <button key={f} onClick={() => setFilter(f)}
                                                    className={`px-3 py-1 rounded-md text-[12px] font-semibold capitalize transition-colors ${filter === f ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                                                    {f}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Invite form */}
                                    <form onSubmit={handleInvite} className="flex gap-2.5 mt-4">
                                        <div className="flex-1 flex items-center gap-2 px-3.5 rounded-xl border border-[#E6E8EE] focus-within:border-[#3F57E0] transition-colors">
                                            <Mail size={15} className="text-slate-400 shrink-0" />
                                            <input type="text" value={inviteInput} onChange={(e) => setInviteInput(e.target.value)}
                                                placeholder="friend@company.com"
                                                className="flex-1 py-2.5 text-[13.5px] text-slate-800 bg-transparent focus:outline-none" />
                                        </div>
                                        <button type="submit" disabled={inviting || !inviteInput.trim()}
                                            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#3F57E0] text-white text-[13.5px] font-bold hover:bg-[#3349c9] transition-colors disabled:opacity-60 shrink-0">
                                            <Send size={15} /> {inviting ? 'Sending…' : 'Send invite'}
                                        </button>
                                    </form>

                                    {/* List */}
                                    <div className="mt-3 flex flex-col">
                                        {loading ? (
                                            [0, 1, 2, 3].map((i) => <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse mb-1.5" />)
                                        ) : shown.length === 0 ? (
                                            <div className="text-center text-[13px] text-slate-400 py-10">No referrals yet — send your first invite above.</div>
                                        ) : shown.map((r) => {
                                            const meta = STATUS_META[r.status] || STATUS_META.sent;
                                            const name = r.inviteeName || (r.inviteeEmail ? r.inviteeEmail.split('@')[0] : 'Invited friend');
                                            return (
                                                <div key={r._id} className="flex items-center gap-3 py-3 border-b border-[#F1F3F7] last:border-0">
                                                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[13px] font-bold shrink-0" style={{ background: avatarBg(name) }}>
                                                        {initials(name)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-[13.5px] font-bold text-slate-900 truncate">{name}</p>
                                                        <p className="text-[12px] text-slate-400 truncate">{r.inviteeEmail}</p>
                                                    </div>
                                                    <div className="shrink-0 text-center w-[120px] hidden sm:block">
                                                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded ${meta.badgeCls}`}>{meta.badge}</span>
                                                        <p className="text-[11px] text-slate-400 mt-1">{meta.sub}</p>
                                                    </div>
                                                    <div className="shrink-0 text-right w-[100px]">
                                                        {meta.kind === 'earned'
                                                            ? <p className="text-[13px] font-bold text-emerald-600">+{(r.rewardCredits || 0).toLocaleString()} credits</p>
                                                            : meta.kind === 'pending'
                                                                ? <p className="text-[13px] font-bold text-slate-700">Pending</p>
                                                                : <p className="text-[13px] font-bold text-slate-300">—</p>}
                                                        <p className="text-[11px] text-slate-400 mt-1">{timeAgo(r.updatedAt || r.createdAt)}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {filtered.length > 6 && (
                                        <div className="flex items-center justify-between pt-3 mt-1 text-[12.5px]">
                                            <span className="text-slate-400">Showing {shown.length} of {filtered.length} invites</span>
                                            <button onClick={() => setShowAll((v) => !v)}
                                                className="font-semibold text-[#3F57E0] hover:underline inline-flex items-center gap-1">
                                                {showAll ? 'Show less' : 'View all'} <ChevronRight size={14} className={`transition-transform ${showAll ? '-rotate-90' : ''}`} />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Right rail */}
                                <div className="flex flex-col gap-5">
                                    {/* How it works */}
                                    <div className="bg-white rounded-2xl border border-[#ECECEC] p-5">
                                        <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400 mb-3.5">How it works</h3>
                                        <div className="flex flex-col gap-3.5">
                                            {HOW_IT_WORKS.map((s, i) => (
                                                <div key={i} className="flex items-start gap-3">
                                                    <div className="w-6 h-6 rounded-full bg-indigo-50 text-[#3F57E0] flex items-center justify-center text-[12px] font-bold shrink-0">{i + 1}</div>
                                                    <div>
                                                        <p className="text-[13px] font-bold text-slate-800 leading-tight">{s.t}</p>
                                                        <p className="text-[12px] text-slate-400 mt-0.5 leading-snug">{s.d}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* FAQs */}
                                    <div className="bg-white rounded-2xl border border-[#ECECEC] p-5">
                                        <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400 mb-1">FAQs</h3>
                                        <div className="flex flex-col">
                                            {FAQS.map((item, i) => {
                                                const isOpen = openFaq === i;
                                                return (
                                                    <div key={i} className="border-b border-[#F1F3F7] last:border-0">
                                                        <button
                                                            onClick={() => setOpenFaq(isOpen ? null : i)}
                                                            aria-expanded={isOpen}
                                                            className="w-full flex items-center justify-between gap-2 py-2.5 text-left">
                                                            <span className="text-[12.5px] font-medium text-slate-700">{item.q}</span>
                                                            <ChevronRight size={15} className={`text-slate-300 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                                                        </button>
                                                        {isOpen && (
                                                            <p className="text-[12px] text-slate-500 leading-relaxed pb-3 pr-5">{item.a}</p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReferEarn;
